import { supabase } from '../supabaseClient';
import { toLocalIsoDate } from '../utils/localDate';

export const CICLO_ABERTO_STATUS = ['planejado', 'ativo'];

const dataHoje = () => toLocalIsoDate();

export function statusDoNovoCiclo(dataPlantio, hoje = dataHoje()) {
  return dataPlantio > hoje ? 'planejado' : 'ativo';
}

export function validarNovoCicloCultivo(form, cicloAtual = null) {
  if (cicloAtual && CICLO_ABERTO_STATUS.includes(cicloAtual.status)) {
    return 'Este talhão já possui um plantio aberto. Registre a colheita antes de iniciar outro.';
  }
  if (!String(form?.cultura || '').trim() || String(form.cultura).trim().length < 2) {
    return 'Informe a cultura do novo plantio.';
  }
  if (!form?.data_plantio) return 'Informe a data de início do plantio.';
  if (form?.data_colheita_prevista && form.data_colheita_prevista < form.data_plantio) {
    return 'A previsão de colheita não pode ser anterior ao plantio.';
  }
  return '';
}

export function validarEdicaoCicloCultivo(ciclo, form) {
  if (!ciclo?.id || String(ciclo.id).startsWith('legado-')) {
    return 'O plantio selecionado ainda não está disponível para edição.';
  }
  if (!CICLO_ABERTO_STATUS.includes(ciclo.status)) {
    return 'Somente um plantio planejado ou ativo pode ser editado.';
  }
  if (!String(form?.cultura || '').trim() || String(form.cultura).trim().length < 2) {
    return 'Informe a cultura do plantio.';
  }
  if (!form?.data_plantio) return 'Informe a data de início do plantio.';
  if (form?.data_colheita_prevista && form.data_colheita_prevista < form.data_plantio) {
    return 'A previsão de colheita não pode ser anterior ao plantio.';
  }
  return '';
}

export function validarRegistroColheita(ciclo, dataColheita) {
  if (!ciclo?.id) return 'Nenhum plantio aberto foi encontrado.';
  if (!dataColheita) return 'Informe a data efetiva da colheita.';
  if (dataColheita < ciclo.data_plantio) return 'A colheita não pode ser anterior ao plantio.';
  return '';
}

export function contextoCultivoLegado(talhao) {
  if (!talhao?.data_plantio || !talhao?.cultura) return { cicloAtual: null, ultimoCiclo: null };
  const colheita = talhao.data_colheita || null;
  const colhido = Boolean(colheita && colheita <= dataHoje());
  const ciclo = {
    id: `legado-${talhao.id}`,
    talhao_id: talhao.id,
    propriedade_id: talhao.propriedade_id,
    cultura: talhao.cultura,
    data_plantio: talhao.data_plantio,
    data_colheita_prevista: colhido ? null : colheita,
    data_colheita: colhido ? colheita : null,
    status: colhido ? 'colhido' : statusDoNovoCiclo(talhao.data_plantio),
    origem: 'compatibilidade_local',
  };
  return { cicloAtual: colhido ? null : ciclo, ultimoCiclo: ciclo };
}

export function aplicarContextoCultivoAoTalhao(talhao, contexto = {}) {
  const cicloAtual = contexto.cicloAtual || null;
  const ultimoCiclo = contexto.ultimoCiclo || cicloAtual || null;
  const cicloCompatibilidade = cicloAtual || ultimoCiclo;
  // Conserva todos os ciclos retornados pela consulta, não só o último.
  // Atualizações locais substituem o ciclo salvo sem apagar os anteriores.
  const ciclos = Array.isArray(contexto.ciclos)
    ? contexto.ciclos
    : cicloCompatibilidade
      ? [...new Map([...(talhao.ciclos_cultivo || []), ultimoCiclo, cicloAtual]
        .filter(Boolean).map(ciclo => [String(ciclo.id), ciclo])).values()]
      : [];
  return {
    ...talhao,
    ciclo_atual: cicloAtual,
    ultimo_ciclo: ultimoCiclo,
    ciclos_cultivo: ciclos,
    cultura: cicloCompatibilidade?.cultura || null,
    data_plantio: cicloCompatibilidade?.data_plantio || null,
    data_colheita: cicloCompatibilidade?.data_colheita || cicloCompatibilidade?.data_colheita_prevista || null,
    data_colheita_prevista: cicloAtual?.data_colheita_prevista || null,
    data_colheita_efetiva: cicloCompatibilidade?.data_colheita || null,
  };
}

export function camposLegadosDoCiclo(ciclo) {
  if (!ciclo) return { cultura: null, data_plantio: null, data_colheita: null };
  return {
    cultura: ciclo.cultura || null,
    data_plantio: ciclo.data_plantio || null,
    data_colheita: ciclo.status === 'colhido'
      ? (ciclo.data_colheita || null)
      : (ciclo.data_colheita_prevista || null),
  };
}

async function sincronizarCamposLegadosDoTalhao(ciclo) {
  const talhaoId = ciclo?.talhao_id;
  if (!talhaoId) throw new Error('O plantio foi salvo sem a identificação do talhão. Atualize a tela e tente novamente.');

  const { error } = await supabase
    .from('talhoes')
    .update(camposLegadosDoCiclo(ciclo))
    .eq('id', talhaoId);
  if (error) {
    throw new Error(`O plantio foi salvo, mas não foi possível sincronizá-lo com Propriedades e Talhões: ${error.message}`);
  }
}

async function sincronizarTalhaoAposExclusao(talhaoId, ciclo = null) {
  if (!talhaoId) throw new Error('O plantio não possui um talhão válido. Atualize a tela e tente novamente.');

  const { error } = await supabase
    .from('talhoes')
    .update(camposLegadosDoCiclo(ciclo))
    .eq('id', talhaoId);
  if (error) {
    throw new Error(`O plantio foi excluído, mas não foi possível atualizar o talhão: ${error.message}`);
  }
}

export async function buscarContextosCultivo(talhaoIds = []) {
  const ids = [...new Set(talhaoIds.filter(Boolean))];
  if (!ids.length) return new Map();

  const { data, error } = await supabase
    .from('ciclos_cultivo')
    .select('*')
    .in('talhao_id', ids)
    .neq('status', 'cancelado')
    .order('data_plantio', { ascending: false })
    .order('id', { ascending: false });
  if (error) throw error;

  const contextos = new Map();
  (data || []).forEach(ciclo => {
    const chave = String(ciclo.talhao_id);
    const atual = contextos.get(chave) || { cicloAtual: null, ultimoCiclo: null, ciclos: [] };
    atual.ciclos.push(ciclo);
    if (!atual.ultimoCiclo) atual.ultimoCiclo = ciclo;
    if (!atual.cicloAtual && CICLO_ABERTO_STATUS.includes(ciclo.status)) atual.cicloAtual = ciclo;
    contextos.set(chave, atual);
  });
  return contextos;
}

export async function criarCicloCultivo({ talhao, userId, form, cicloAtual = null }) {
  const validationError = validarNovoCicloCultivo(form, cicloAtual);
  if (validationError) throw new Error(validationError);
  if (!talhao?.id || !talhao?.propriedade_id || !userId) throw new Error('Contexto do talhão ou usuário indisponível.');

  const payload = {
    talhao_id: talhao.id,
    propriedade_id: talhao.propriedade_id,
    user_id: userId,
    cultura: String(form.cultura).trim(),
    variedade: String(form.variedade || '').trim() || null,
    data_plantio: form.data_plantio,
    data_colheita_prevista: form.data_colheita_prevista || null,
    status: statusDoNovoCiclo(form.data_plantio),
    observacoes: String(form.observacoes || '').trim() || null,
    origem: 'central_operacoes',
  };

  const { data, error } = await supabase
    .from('ciclos_cultivo')
    .insert(payload)
    .select('*')
    .single();
  if (error?.code === '23505') {
    throw new Error('Este talhão já possui um plantio aberto. Registre a colheita antes de iniciar outro.');
  }
  if (error) throw error;
  await sincronizarCamposLegadosDoTalhao(data);
  return data;
}

function operationalPlantingPayload(form) {
  return {
    p_cultura: String(form.cultura).trim(),
    p_variedade: String(form.variedade || '').trim() || null,
    p_data_plantio: form.data_plantio,
    p_data_colheita_prevista: form.data_colheita_prevista || null,
    p_observacoes: String(form.observacoes || '').trim() || null,
  };
}

export async function criarPlantioOperacional({ talhao, form, cicloAtual = null }) {
  const validationError = validarNovoCicloCultivo(form, cicloAtual);
  if (validationError) throw new Error(validationError);
  const plotId = talhao?._physicalPlotId ?? talhao?.id;
  if (!plotId) throw new Error('Talhão físico indisponível.');
  const { data, error } = await supabase.rpc('criar_plantio_operacional_usuario', {
    p_talhao_fisico_id: plotId,
    ...operationalPlantingPayload(form),
  });
  if (error) throw error;
  return data;
}

export async function editarPlantioOperacional({ ciclo, form }) {
  const validationError = validarEdicaoCicloCultivo(ciclo, form);
  if (validationError) throw new Error(validationError);
  const { data, error } = await supabase.rpc('atualizar_plantio_operacional_usuario', {
    p_plantio_id: ciclo.id,
    ...operationalPlantingPayload(form),
  });
  if (error) throw error;
  return data;
}

export async function registrarColheitaOperacional({ ciclo, dataColheita }) {
  const validationError = validarRegistroColheita(ciclo, dataColheita);
  if (validationError) throw new Error(validationError);
  const { data, error } = await supabase.rpc('registrar_colheita_operacional_usuario', {
    p_plantio_id: ciclo.id,
    p_data_colheita: dataColheita,
  });
  if (error) throw error;
  return data;
}

export async function editarCicloCultivo({ ciclo, form }) {
  const validationError = validarEdicaoCicloCultivo(ciclo, form);
  if (validationError) throw new Error(validationError);

  const payload = {
    cultura: String(form.cultura).trim(),
    variedade: String(form.variedade || '').trim() || null,
    data_plantio: form.data_plantio,
    data_colheita_prevista: form.data_colheita_prevista || null,
    status: statusDoNovoCiclo(form.data_plantio),
    observacoes: String(form.observacoes || '').trim() || null,
  };

  const { data, error } = await supabase
    .from('ciclos_cultivo')
    .update(payload)
    .eq('id', ciclo.id)
    .in('status', CICLO_ABERTO_STATUS)
    .select('*')
    .single();
  if (error) throw error;
  await sincronizarCamposLegadosDoTalhao(data);
  return data;
}

export async function registrarColheitaCultivo({ ciclo, dataColheita }) {
  const validationError = validarRegistroColheita(ciclo, dataColheita);
  if (validationError) throw new Error(validationError);

  const { data, error } = await supabase
    .from('ciclos_cultivo')
    .update({ status: 'colhido', data_colheita: dataColheita })
    .eq('id', ciclo.id)
    .in('status', CICLO_ABERTO_STATUS)
    .select('*')
    .single();
  if (error) throw error;
  await sincronizarCamposLegadosDoTalhao(data);
  return data;
}

export async function excluirCicloCultivo({ ciclo }) {
  if (!ciclo?.id || String(ciclo.id).startsWith('legado-')) {
    throw new Error('Este plantio ainda não possui um registro individual que possa ser excluído.');
  }

  const { data, error } = await supabase.rpc('excluir_ciclo_cultivo_usuario', {
    p_ciclo_id: ciclo.id,
  });
  if (error) throw error;
  if (data?.excluido !== true || !data?.talhao_id) {
    throw new Error('O banco não confirmou a exclusão segura do plantio.');
  }

  const contextos = await buscarContextosCultivo([data.talhao_id]);
  const contexto = contextos.get(String(data.talhao_id)) || { cicloAtual: null, ultimoCiclo: null };
  await sincronizarTalhaoAposExclusao(data.talhao_id, contexto.cicloAtual || contexto.ultimoCiclo);

  return {
    talhaoId: data.talhao_id,
    contexto,
  };
}
